import { utils, LitElement, html, broadcastReceiver } from '@hh/global.js';

const { serializeForm, addToCart, getCart, reRenderShopifySections } = utils

class HHProductForm extends LitElement {
  static properties = {
    showMiniCartAfterSubmission: {
      type: Boolean,
      attribute: 'show-mini-cart',
    },
    updateSubmitText: {
      type: Boolean,
      attribute: 'update-submit-text',
    },
    productTitle: {
      type: String,
      attribute: 'product-title',
    },
    productLink: {
      type: String,
      attribute: 'product-link',
    },
  }

  constructor() {
    super()

    this.classes = {
      active: 'active',
    }
  }

  connectedCallback() {
    super.connectedCallback()

    this.form = this.querySelector('form')
    this.quanityInput = this.querySelector('[name="quantity"]')
    this.submitButton = this.querySelector('[type="submit"]')
    this.errorMessage = this.querySelector('[data-error-message]')

    this.form.addEventListener('submit', this.onSubmitHandler.bind(this))

    this.receiver = broadcastReceiver('quantity', (...args) => {
      this.getQuantityFromDocument(...args)
    })

    this.addEventListener(
      'quantityRangeError',
      this.onQuantityRangeError.bind(this)
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver?.disconnect()

    this.removeEventListener('quantityRangeError', this.onQuantityRangeError)
  }

  getQuantityFromDocument(data) {
    if (this.quanityInput) {
      this.quanityInput.value = data.value || 1
    }
  }

  async onSubmitHandler(event) {
    event.preventDefault()

    this.submitButton.setAttribute('disabled', true)
    this.submitButton.classList.add('loading')

    if (this.updateSubmitText) {
      this.submitButton.textContent = window.variantStrings.addingToCart
    }

    const jsonForm = JSON.parse(serializeForm(this.form))

    const isMaxQuantity = await this.checkMaxQuantity(jsonForm)

    if (isMaxQuantity) {
      this.dispatchEvent(
        new CustomEvent('productError', {
          bubbles: true,
          composed: true,
          detail: {
            id: 'error-notification',
            auto_close: 2500,
          },
        })
      )
      this.revertAddToCartButton()
    } else {
      try {
        const cart_item = await addToCart(jsonForm)
        await reRenderShopifySections(cart_item)

        if (this.updateSubmitText) {
          this.submitButton.textContent = window.variantStrings.addedToCart
        }

        if (this.showMiniCartAfterSubmission) {
          utils.toggle(document.body, ['mini-cart-is-active'])
        }

        if (window.gwp.enabled) {
          this.dispatchEvent(
            new CustomEvent('productAddedGWP', {
              bubbles: true,
              composed: true,
              detail: {
                line: cart_item
              },
            })
          )
        }

        this.dispatchEvent(
          new CustomEvent('productAdded', {
            bubbles: true,
            composed: true,
            detail: {
              id: 'cart-notification',
              product_title: this.productTitle,
              auto_close: 2500,
            },
          })
        )
        window.dispatchEvent(
          new CustomEvent('cart-message:added', {
            bubbles: true,
            composed: true,
            detail: {
              title: this.productTitle,
              link: this.productLink,
              variant_id: cart_item.variant_id,
              price: cart_item.price,
              quantity: cart_item.quantity,
            },
          })
        )
      } catch (error) {
        this.showErrorMessage(error.toString())
      } finally {
        this.revertAddToCartButton()
      }
    }
  }

  revertAddToCartButton() {
    if (this.updateSubmitText) {
      setTimeout(() => {
        this.submitButton.removeAttribute('disabled')
        this.submitButton.classList.remove('loading')
        this.submitButton.textContent = window.variantStrings.addToCart
      }, 500)
    } else {
      this.submitButton.removeAttribute('disabled')
    }
  }

  async checkMaxQuantity(form) {
    const cart = await getCart()
    const { items } = cart
    const { id: formId, quantity: formQuantity } = form

    const matchingCartItems = items.filter(
      (item) => item.id === parseInt(formId)
    )
    const { cartMaxQuantity } = window.themeSettings

    if (matchingCartItems.length === 0 && formQuantity > cartMaxQuantity) {
      return true
    } else if (matchingCartItems.length > 0) {
      const cartQuantity = matchingCartItems.reduce(
        (acc, item) => acc + item.quantity,
        0
      )

      if (parseInt(formQuantity) + cartQuantity > cartMaxQuantity) {
        return true
      }
    }

    return false
  }

  onQuantityRangeError(event) {
    const { detail } = event
    const { message, clearMessage } = detail

    this.showErrorMessage(message, clearMessage)
  }

  showErrorMessage(message, clearMessage) {
    if (clearMessage) {
      this.errorMessage.textContent = ''
      this.errorMessage.classList.remove(this.classes.active)
      return
    }

    this.errorMessage.textContent = message
    this.errorMessage.classList.add(this.classes.active)

    if (this.errorTimeout) clearTimeout(this.errorTimeout)

    this.errorTimeout = setTimeout(() => {
      this.errorMessage.classList.remove(this.classes.active)
    }, 3500)
  }

  render() {
    return html`<slot></slot>`
  }
}

if (!customElements.get('hh-product-form')) {
  customElements.define('hh-product-form', HHProductForm);
}
